import { useCallback, useRef, useState } from 'react';
import { MultiPageImageModel, MultiPageVideoModel, TextElement, TextRenderer } from '@bynder-studio/render-core';
import { Variation } from './types';

// variationId -> overset creativeVersionPageIds; variation-level overset is derived from it
type OversetPagesMap = Map<string, Set<number>>;
type MapUpdater = OversetPagesMap | ((prev: OversetPagesMap) => OversetPagesMap);

const isSameSet = (a: Set<number>, b: Set<number>) => a.size === b.size && [...a].every((id) => b.has(id));

const isSameMap = (a: OversetPagesMap, b: OversetPagesMap) => {
    if (a.size !== b.size) {
        return false;
    }

    for (const [key, set] of a) {
        const other = b.get(key);

        if (!other || !isSameSet(set, other)) {
            return false;
        }
    }

    return true;
};

export const useOversetValidation = ({
    creativeModel,
}: {
    creativeModel?: MultiPageVideoModel | MultiPageImageModel;
}) => {
    const oversetPagesRef = useRef<OversetPagesMap>(new Map());
    const [oversetPagesByVariation, setOversetPagesState] = useState(oversetPagesRef.current);

    const setOversetPagesByVariation = useCallback((updater: MapUpdater) => {
        const prev = oversetPagesRef.current;
        const next = typeof updater === 'function' ? updater(prev) : updater;

        if (isSameMap(prev, next)) {
            return;
        }

        oversetPagesRef.current = next;
        setOversetPagesState(next);
    }, []);

    // Checks every page (no early exit) so partial validity is captured
    const getOversetPageIds = useCallback(
        (variation: Variation, textRenderer = TextRenderer.getInstance()): Set<number> => {
            const oversetPages = new Set<number>();

            if (!creativeModel) {
                return oversetPages;
            }

            const models = creativeModel.getModels();

            creativeModel.getModelsMetaData()?.forEach(({ id: pageId }, pageIndex) => {
                const elements = variation?.sizes?.[pageId]?.elements;
                const pageModel = models[pageIndex];

                if (!elements || !pageModel) {
                    return;
                }

                const hasOversetText = Object.entries(elements).some(([elId, value]) => {
                    const { formattedText } = value as any;

                    if (!formattedText) {
                        return false;
                    }

                    const element = pageModel.getElementById(Number(elId)) as TextElement;

                    if (!element || !element.limitTextToBounds) {
                        return false;
                    }

                    const {
                        dimension: { width, height },
                        contentTransform,
                        textControl,
                        minFontScale,
                        fontScale,
                    } = element;
                    const config = {
                        boxWidth: width,
                        boxHeight: height,
                        placement: textControl,
                        horizontalAlignment: contentTransform?.horizontalAlignment,
                        verticalAlignment: contentTransform?.verticalAlignment,
                        textStruct: formattedText,
                        minFontScale,
                        fontScale,
                    };

                    const boundedText = textRenderer.createBoundedText(formattedText, config);

                    return (
                        boundedText.getShapedText().getContentHeight() > height ||
                        boundedText.getShapedText().getContentWidth() > width
                    );
                });

                if (hasOversetText) {
                    oversetPages.add(Number(pageId));
                }
            });

            return oversetPages;
        },
        [creativeModel],
    );

    const updateAllVariationsOverset = useCallback(
        (variations: Variation[]) => {
            const textRenderer = TextRenderer.getInstance();
            const next: OversetPagesMap = new Map();

            variations.forEach((variation) => {
                const oversetPages = getOversetPageIds(variation, textRenderer);

                if (oversetPages.size) {
                    next.set(variation.variationSetId, oversetPages);
                }
            });

            setOversetPagesByVariation(next);
        },
        [getOversetPageIds, setOversetPagesByVariation],
    );

    // Optimistic toggle for the current page while editing; getOversetPageIds recomputes on save
    const updateVariationOverset = useCallback(
        ({ isValid, currentVariation }: { isValid: boolean; currentVariation: string }) => {
            if (!currentVariation || !creativeModel) {
                return;
            }

            const pageIndex = creativeModel.getCurrentPageIndex();
            const pageId = creativeModel.getModelsMetaData()?.[pageIndex]?.id;

            if (pageId === undefined || pageId === null) {
                return;
            }

            const numericPageId = Number(pageId);

            setOversetPagesByVariation((prev) => {
                const next = new Map(prev);
                const pages = new Set(next.get(currentVariation));

                if (isValid) {
                    pages.delete(numericPageId);
                } else {
                    pages.add(numericPageId);
                }

                if (pages.size) {
                    next.set(currentVariation, pages);
                } else {
                    next.delete(currentVariation);
                }

                return next;
            });
        },
        [creativeModel, setOversetPagesByVariation],
    );

    const isVariationOverset = useCallback(
        (variationSetId: string) => (oversetPagesByVariation.get(variationSetId)?.size ?? 0) > 0,
        [oversetPagesByVariation],
    );

    const isPageOverset = useCallback(
        (variationSetId: string, pageId: number) =>
            Boolean(oversetPagesByVariation.get(variationSetId)?.has(Number(pageId))),
        [oversetPagesByVariation],
    );

    return {
        oversetPagesByVariation,
        oversetPagesRef,
        setOversetPagesByVariation,
        getOversetPageIds,
        updateVariationOverset,
        updateAllVariationsOverset,
        isVariationOverset,
        isPageOverset,
    };
};
